// Defence in depth for the 2026-09-29 incident: the API returned each responding
// expert's full users row (password hash, refresh and reset tokens, login email,
// Stripe customer id) on the public alert response endpoints, and this site put it
// in the __NEXT_DATA__ of every ISR alert page and every embed page. The API now
// whitelists these, but whatever reaches getStaticProps/getServerSideProps is
// published, so the pages keep only what they render.

// Never legitimately needed on a public alert page, at any depth.
const FORBIDDEN_KEY = /password|token|customerId|^stats|resetPassword/i;

const scrub = (value) => {
    if (Array.isArray(value)) {
        return value.map(scrub);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value)
                .filter(([key]) => !FORBIDDEN_KEY.test(key))
                .map(([key, child]) => [key, scrub(child)])
        );
    }
    return value;
};

// PostAlert.js and Card.js read name, publication, isAnonymised, isFTEditor and
// roles[].name.
// findBySlug answers `false` when the author is hidden; keep that as is.
export const publicAlertAuthor = (author) => {
    if (!author) {
        return author ?? null;
    }
    const picked = {};
    for (const key of [
        'id',
        'slug',
        'name',
        'type',
        'publication',
        'isFTEditor',
        'isAnonymised',
    ]) {
        if (author[key] !== undefined) {
            picked[key] = author[key];
        }
    }
    // An API from before 12 Oct 2026 still flags FT here, and FT Adviser names the publication.
    if (author.isAnonymised && picked.isFTEditor) {
        picked.isFTEditor = false;
    }
    if (Array.isArray(author.roles)) {
        picked.roles = author.roles.map((role) => ({
            name: role?.name ?? null,
        }));
    }
    return picked;
};

// Response.js reads author.isVerified and author.plan.type; ResponseDetail.js reads
// author.plan.type.
export const publicResponseAuthor = (author) =>
    author
        ? {
              id: author.id ?? null,
              isVerified: author.isVerified ?? null,
              plan: author.plan ? { type: author.plan.type ?? null } : null,
          }
        : null;

// A journalist hidden from logged-out visitors: their profile and user ids would name them
// through the public profile endpoint, and their source page sits on their outlet's site.
const withoutHiddenAuthorIds = (alert) => {
    if (!alert.author?.isAnonymised) {
        return alert;
    }
    const {
        profileId,
        authorId,
        lastEditedBy,
        sourceUrl,
        sourceLabel,
        sourceBy,
        ...rest
    } = alert;
    return rest;
};

// Logged-out visitors and crawlers must not learn that a story sponsorship is on offer
// (ResearchSponsorBox fetches it for logged-in experts); the page keeps only the sold credit.
const publicResearch = (research) =>
    research?.status === 'sold'
        ? {
              researchSponsorship: {
                  status: 'sold',
                  sponsorName: research.sponsorName ?? null,
                  sponsorWebsite: research.sponsorWebsite ?? null,
              },
          }
        : {};

export const publicAlert = (alert) => {
    if (!alert) {
        return alert;
    }
    const { researchSponsorship, ...rest } = withoutHiddenAuthorIds(alert);
    return scrub({
        ...rest,
        ...('author' in alert
            ? { author: publicAlertAuthor(alert.author) }
            : {}),
        ...publicResearch(researchSponsorship),
    });
};

export const publicResponse = (response) =>
    response
        ? scrub({
              ...response,
              author: publicResponseAuthor(response.author),
              ...(response.alert ? { alert: publicAlert(response.alert) } : {}),
          })
        : response;

export const publicResponses = (responses) =>
    Array.isArray(responses) ? responses.map(publicResponse) : responses;
